
import {
    CategoryModel,
    PlanItemBasicModel,
    PlanItemModel,
    ProcessTypeEnum,
    ResponseModel,
    ResponseTypeEnum,
    TenantUserModel,
} from '@/libs/Api';
import { CrudAction } from '@/libs/core/+state/models/crud-action';
import { CrudDataStats } from '@/libs/core/+state/models/crud-data-stats';
import { CrudGetter } from '@/libs/core/+state/models/crud-getter';
import { createCrudQueryPayload } from '@/libs/core/+state/models/crud-query-payload';
import { CrudQueryPredicate } from '@/libs/core/+state/models/crud-query-predicate';
import { planItemsStore } from '@/libs/plan-items/+state/store';
import Page from '@/Page.vue';
import { Component, Prop, Watch } from 'vue-property-decorator';
import { responsesShowDetailsStore, ResponsesShowDetailTypeEnum, responsesStore } from '../+state/store';
import { categoriesStore } from '@/libs/categories/+state/store';
import { authStore } from '@/libs/auth/+store/store';
import { AuthGetter } from '@/libs/auth/models/auth-state';
import LocationSelect from '@/libs/common/components-smart/LocationSelect.vue';
import WorkplaceSelect from '@/libs/common/components-smart/WorkplaceSelect.vue';
import { workplacesStore } from '@/libs/workplaces/+state/store';
import { locationsStore } from '@/libs/locations/+state/store';

@Component({
    computed: {
        ResponsesShowDetailTypeEnum() {
            return ResponsesShowDetailTypeEnum;
        },
    },
    components: { LocationSelect, WorkplaceSelect },
})
export default class Responses extends Page {
    filterCategory: Array<string> | null = null;
    filterAudit: Array<string> | null = null;
    filterUser: Array<string> | null = null;
    filterDepartment: Array<string> | null = null;
    filterPosition: Array<string> | null = null;
    filterLocation: Array<string> | null = null;
    filterWorkplace: Array<string> | null = null;
    filterStation: Array<string> | null = null;
    filterGroup: Array<string> | null = null;
    filterState = 'all';
    filterEvidenceNumber: string | null = null;
    filterPlanItemState: Array<string> | null = null;
    filterCorrectiveMeasure = 0;
    filterIsImplementationClosed = null;
    filterResponseValues: string[] = [];
    filterDelay: number | null = null;
    filterOnlyMine = true;
    mobileFiltersShow = false;
    escalation = 0;
    pageIndex = 1;
    pageSize = 25;
    pagingChanged = false;
    showSecondLine = false;
    debounce: any = null;
    tenantUser: TenantUserModel = authStore.useGetter(AuthGetter.GetTenantUser) as TenantUserModel;

    get workplacesFilterByLocations(): string[] {
        return this.computeExpandedLocationIds();
    }

    get ResponsesShowDetailTypeEnum() {
        return ResponsesShowDetailTypeEnum;
    }

    computeExpandedLocationIds(): string[] {
        const selected = this.filterLocation ?? [];
        if (selected.length === 0) return [];
        const locations = (locationsStore.useGetter(CrudGetter.Data) ?? []) as any[];
        const selectedSet = new Set<string>(selected);
        const descendantIds = locations
            .filter(
                (loc: any) =>
                    Array.isArray(loc.locationParents) &&
                    loc.locationParents.some((p: any) => p.parentalLocationId && selectedSet.has(p.parentalLocationId))
            )
            .map((loc: any) => loc.id)
            .filter(Boolean);
        return Array.from(new Set<string>([...selected, ...descendantIds]));
    }

    get headers() {
        let headers = [
            { text: this.$i18n.t('planItems.time'), value: 'time', align: 'left', sortable: false },
            { text: this.$i18n.t('planItems.audit.name'), value: 'audit.name', align: 'left', sortable: false },
            { text: this.$i18n.t('planItems.evidenceNumber'), value: 'evidenceNumber', align: 'left', sortable: false },
            { text: this.$i18n.t('planItems.category.name'), value: 'audit.category.name', align: 'left', sortable: false },
            {
                text: this.$i18n.t('planItems.user.displayName'),
                value: 'tenantUser.user.displayName',
                align: 'left',
                sortable: false,
            },
            { text: this.$i18n.t('planItems.userGroup'), value: '', align: 'left', sortable: false }, // Skupina uživatelů
            { text: this.$i18n.t('planItems.workplace.name'), value: 'workplace.name', align: 'left', sortable: false },
            { text: this.$i18n.t('planItems.station.name'), value: 'station.name', align: 'left', sortable: false },
            { text: this.$i18n.t('planItems.shiftType.name'), value: 'shiftType.name', align: 'left', sortable: false },
            {
                text: this.$i18n.t('planItems.responseCount'),
                value: 'responseCount',
                align: 'left',
                width: '5%',
                sortable: false,
            },
        ];

        if (
            (this.isOk || this.isOkNok || this.isOkNokWarning || this.isNokWarning) &&
            this.getShowInfo(ResponsesShowDetailTypeEnum.ShowScore)
        )
            headers.push({
                text: this.$i18n.t('planItems.okCount'),
                value: 'okCount',
                align: 'left',
                width: '5%',
                sortable: false,
            });

        if ((this.isOkNokWarning || this.isNokWarning) && this.getShowInfo(ResponsesShowDetailTypeEnum.ShowScore))
            headers.push({
                text: this.$i18n.t('planItems.okCountWithWarning'),
                value: 'nokCount',
                align: 'left',
                width: '5%',
                sortable: false,
            });

        if (
            (this.isOk || this.isOkNok || this.isOkNokWarning || this.isNokWarning) &&
            this.getShowInfo(ResponsesShowDetailTypeEnum.ShowScore)
        )
            headers.push({
                text: this.$i18n.t('planItems.nokCount'),
                value: 'nokCount',
                align: 'left',
                width: '5%',
                sortable: false,
            });

        if (
            (this.isScoreZeroToOne ||
                this.isScoreZeroToTwo ||
                this.isScoreZeroToFour ||
                this.isScoreZeroToFive ||
                this.isScoreZeroToThree ||
                this.isVDA63) &&
            this.getShowInfo(ResponsesShowDetailTypeEnum.ShowScore)
        )
            headers.push({
                text: this.$i18n.t('common.scoreShort') + ' 0',
                value: 'nokCount',
                align: 'left',
                width: '4%',
                sortable: false,
            });

        if (
            (this.isScoreZeroToOne ||
                this.isScoreZeroToTwo ||
                this.isScoreZeroToFour ||
                this.isScoreZeroToFive ||
                this.isScoreOneToThree ||
                this.isScoreOneToFive ||
                this.isScoreZeroToThree ||
                this.isScoreOneToFour) &&
            this.getShowInfo(ResponsesShowDetailTypeEnum.ShowScore)
        )
            headers.push({
                text: this.$i18n.t('common.scoreShort') + ' 1',
                value: 'nokCount',
                align: 'left',
                width: '4%',
                sortable: false,
            });

        if (
            (this.isScoreZeroToTwo ||
                this.isScoreZeroToFour ||
                this.isScoreZeroToFive ||
                this.isScoreOneToThree ||
                this.isScoreOneToFive ||
                this.isScoreZeroToThree ||
                this.isScoreOneToFour) &&
            this.getShowInfo(ResponsesShowDetailTypeEnum.ShowScore)
        )
            headers.push({
                text: this.$i18n.t('common.scoreShort') + ' 2',
                value: 'nokCount',
                align: 'left',
                width: '4%',
                sortable: false,
            });

        if (
            (this.isScoreZeroToFour ||
                this.isScoreZeroToFive ||
                this.isScoreOneToThree ||
                this.isScoreOneToFive ||
                this.isScoreZeroToThree ||
                this.isScoreOneToFour) &&
            this.getShowInfo(ResponsesShowDetailTypeEnum.ShowScore)
        )
            headers.push({
                text: this.$i18n.t('common.scoreShort') + ' 3',
                value: 'nokCount',
                align: 'left',
                width: '4%',
                sortable: false,
            });

        if (
            (this.isScoreZeroToFour ||
                this.isScoreZeroToFive ||
                this.isScoreOneToFive ||
                this.isScoreOneToFour ||
                this.isVDA63) &&
            this.getShowInfo(ResponsesShowDetailTypeEnum.ShowScore)
        )
            headers.push({
                text: this.$i18n.t('common.scoreShort') + ' 4',
                value: 'nokCount',
                align: 'left',
                width: '4%',
                sortable: false,
            });

        if ((this.isScoreZeroToFive || this.isScoreOneToFive) && this.getShowInfo(ResponsesShowDetailTypeEnum.ShowScore))
            headers.push({
                text: this.$i18n.t('common.scoreShort') + ' 5',
                value: 'nokCount',
                align: 'left',
                width: '4%',
                sortable: false,
            });

        if (this.isVDA63 && this.getShowInfo(ResponsesShowDetailTypeEnum.ShowScore))
            headers.push({
                text: this.$i18n.t('common.scoreShort') + ' 6',
                value: 'nokCount',
                align: 'left',
                width: '4%',
                sortable: false,
            });

        if (this.isVDA63 && this.getShowInfo(ResponsesShowDetailTypeEnum.ShowScore))
            headers.push({
                text: this.$i18n.t('common.scoreShort') + ' 8',
                value: 'nokCount',
                align: 'left',
                width: '4%',
                sortable: false,
            });

        if (this.isVDA63 && this.getShowInfo(ResponsesShowDetailTypeEnum.ShowScore))
            headers.push({
                text: this.$i18n.t('common.scoreShort') + ' 10',
                value: 'nokCount',
                align: 'left',
                width: '4%',
                sortable: false,
            });

        if (
            this.isScoreZeroToOne ||
            this.isScoreZeroToTwo ||
            this.isScoreZeroToFour ||
            this.isScoreZeroToFive ||
            this.isScoreOneToThree ||
            this.isScoreOneToFive ||
            this.isScoreOneToThree ||
            this.isScoreOneToFour
        )
            headers.push({ text: this.$i18n.t('planItems.scoreValue'), value: 'scoreValue', align: 'left', sortable: false });

        headers.push(
            { text: this.$i18n.t('planItems.notAnswered'), value: 'notAnsweredCount', align: 'left', sortable: false },
            { text: this.$i18n.t('planItems.successRate'), value: 'nokCount', width: '3%', align: 'left', sortable: false },
            { text: this.$i18n.t('planItems.until'), value: 'until', align: 'left', sortable: false }, // nový sloupec
            { text: this.$i18n.t('planItems.delayDays'), value: 'delayDays', align: 'left', sortable: false }, // nový sloupec
            { text: this.$i18n.t('planItems.planItemState'), value: 'planItemState', align: 'left', sortable: false },
            { text: '', value: 'id', align: 'left', sortable: false }
        );
        return headers;
    }

    get showScore(): boolean {
        return responsesShowDetailsStore.useGetter(ResponsesShowDetailTypeEnum.ShowScore);
    }

    get delayFilterOptions(): { text: string; value: number }[] {
        return [
            { text: this.$t('measures.oneAndMoreDays').toString(), value: 1 },
            { text: this.$t('measures.twoAndMoreDays').toString(), value: 2 },
            { text: this.$t('measures.threeAndMoreDays').toString(), value: 3 },
            { text: this.$t('measures.fourAndMoreDays').toString(), value: 4 },
            { text: this.$t('measures.sevenAndMoreDays').toString(), value: 7 },
            { text: this.$t('measures.fourteenAndMoreDays').toString(), value: 14 },
            { text: this.$t('measures.thirtyAndMoreDays').toString(), value: 30 },
        ];
    }

    get responseValueItems() {
        return [
            { text: this.$i18n.t('responseValue.ok'), value: 'ok' },
            { text: this.$i18n.t('responseValue.okWithWarning'), value: 'okWithWarning' },
            { text: this.$i18n.t('responseValue.notOk'), value: 'notOk' },
            { text: this.$i18n.t('responseValue.scoreZero'), value: 'scoreZero' },
            { text: this.$i18n.t('responseValue.scoreOne'), value: 'scoreOne' },
            { text: this.$i18n.t('responseValue.scoreTwo'), value: 'scoreTwo' },
            { text: this.$i18n.t('responseValue.scoreThree'), value: 'scoreThree' },
            { text: this.$i18n.t('responseValue.scoreFour'), value: 'scoreFour' },
            { text: this.$i18n.t('responseValue.scoreFive'), value: 'scoreFive' },
            { text: this.$i18n.t('responseValue.scoreSix'), value: 'scoreSix' },
            { text: this.$i18n.t('responseValue.scoreEight'), value: 'scoreEight' },
            { text: this.$i18n.t('responseValue.scoreTen'), value: 'scoreTen' },
        ];
    }

    @Prop() tenantId!: string;

    @Watch('filterPlanItemState')
    filterStateChanged() {
        this.load();
    }

    @Watch('pageIndex')
    @Watch('pageSize')
    paginationChanged() {
        this.saveQuery();
        if (this.pagingChanged) {
            this.pagingChanged = false;
            return;
        }
        this.load();
    }

    @Watch('filterEvidenceNumber')
    @Watch('filterCategory')
    @Watch('filterAudit')
    @Watch('filterUser')
    @Watch('filterDepartment')
    @Watch('filterPosition')
    @Watch('filterLocation')
    @Watch('filterWorkplace')
    @Watch('filterGroup')
    @Watch('filterStation')
    @Watch('filterCorrectiveMeasure')
    @Watch('filterIsImplementationClosed')
    @Watch('filterResponseValues')
    @Watch('filterDelay')
    @Watch('filterOnlyMine')
    filtersChanged() {
        clearTimeout(this.debounce);
        this.debounce = setTimeout(() => {
            this.pageIndex = 1;
            this.escalation--;
            if (this.escalation <= 0) this.load();
        }, 500);
    }

    @Watch('itemsPerPage')
    itemsPerPageChanged(v: number) {
        window.localStorage.amsItemsPerPage = v.toString();
    }

    get loading(): boolean {
        return planItemsStore.useGetter(CrudGetter.PendingBasic);
    }

    get planItems(): PlanItemBasicModel[] {
        return [...planItemsStore.useGetter(CrudGetter.DataBasic)];
    }

    get expandedPlanItems(): PlanItemBasicModel[] {
        const items = planItemsStore.useGetter(CrudGetter.DataBasic) as PlanItemBasicModel[];
        const planItems = Array.isArray(items) ? [...items] : [];
        return planItems.filter((x) => (x as any).aiIsReportGenerated);
    }

    get totalRows(): number {
        return ({ ...planItemsStore.useGetter(CrudGetter.DataStatsBasic) } as CrudDataStats)?.rowCount;
    }

    get downloading(): boolean {
        return responsesStore.useGetter(CrudGetter.Downloading);
    }

    get categories(): CategoryModel[] {
        return [...categoriesStore.useGetter(CrudGetter.Data)];
    }

    get isOk(): boolean {
        let result = this.categories.filter((x: CategoryModel) => x.responseType == ResponseTypeEnum.Ok);
        if (
            (result.length > 0 &&
                this.categoryFilterResponseType &&
                this.categoryFilterResponseType.length > 0 &&
                this.categoryFilterResponseType!.includes(ResponseTypeEnum.Ok)) ||
            this.categoryFilterResponseType == null
        )
            return true;
        else return false;
    }

    get isOkNok(): boolean {
        let result = this.categories.filter((x: CategoryModel) => x.responseType == ResponseTypeEnum.OkNok);
        if (
            (result.length > 0 &&
                this.categoryFilterResponseType &&
                this.categoryFilterResponseType.length > 0 &&
                this.categoryFilterResponseType!.includes(ResponseTypeEnum.OkNok)) ||
            this.categoryFilterResponseType == null
        )
            return true;
        else return false;
    }

    get isOkNokWarning(): boolean {
        let result = this.categories.filter((x: CategoryModel) => x.responseType == ResponseTypeEnum.OkNokWarning);
        if (
            (result.length > 0 &&
                this.categoryFilterResponseType &&
                this.categoryFilterResponseType.length > 0 &&
                this.categoryFilterResponseType!.includes(ResponseTypeEnum.OkNokWarning)) ||
            this.categoryFilterResponseType == null
        )
            return true;
        else return false;
    }

    get isNokWarning(): boolean {
        let result = this.categories.filter((x: CategoryModel) => x.responseType == ResponseTypeEnum.NokWarning);
        if (
            (result.length > 0 &&
                this.categoryFilterResponseType &&
                this.categoryFilterResponseType.length > 0 &&
                this.categoryFilterResponseType!.includes(ResponseTypeEnum.NokWarning)) ||
            this.categoryFilterResponseType == null
        )
            return true;
        else return false;
    }

    get isScoreZeroToFive(): boolean {
        let result = this.categories.filter((x: CategoryModel) => x.responseType == ResponseTypeEnum.ScoreZeroToFive);
        if (
            (result.length > 0 &&
                this.categoryFilterResponseType &&
                this.categoryFilterResponseType.length > 0 &&
                this.categoryFilterResponseType!.includes(ResponseTypeEnum.ScoreZeroToFive)) ||
            this.categoryFilterResponseType == null
        )
            return true;
        else return false;
    }

    get isScoreZeroToFour(): boolean {
        let result = this.categories.filter((x: CategoryModel) => x.responseType == ResponseTypeEnum.ScoreZeroToFour);
        if (
            (result.length > 0 &&
                this.categoryFilterResponseType &&
                this.categoryFilterResponseType.length > 0 &&
                this.categoryFilterResponseType!.includes(ResponseTypeEnum.ScoreZeroToFour)) ||
            this.categoryFilterResponseType == null
        )
            return true;
        else return false;
    }

    get isScoreZeroToOne(): boolean {
        let result = this.categories.filter((x: CategoryModel) => x.responseType == ResponseTypeEnum.ScoreZeroToOne);
        if (
            (result.length > 0 &&
                this.categoryFilterResponseType &&
                this.categoryFilterResponseType.length > 0 &&
                this.categoryFilterResponseType!.includes(ResponseTypeEnum.ScoreZeroToOne)) ||
            this.categoryFilterResponseType == null
        )
            return true;
        else return false;
    }

    get isScoreZeroToTwo(): boolean {
        let result = this.categories.filter((x: CategoryModel) => x.responseType == ResponseTypeEnum.ScoreZeroToTwo);
        if (
            (result.length > 0 &&
                this.categoryFilterResponseType &&
                this.categoryFilterResponseType.length > 0 &&
                this.categoryFilterResponseType!.includes(ResponseTypeEnum.ScoreZeroToTwo)) ||
            this.categoryFilterResponseType == null
        )
            return true;
        else return false;
    }

    get isScoreOneToThree(): boolean {
        let result = this.categories.filter((x: CategoryModel) => x.responseType == ResponseTypeEnum.ScoreOneToThree);
        if (
            (result.length > 0 &&
                this.categoryFilterResponseType &&
                this.categoryFilterResponseType.length > 0 &&
                this.categoryFilterResponseType!.includes(ResponseTypeEnum.ScoreOneToThree)) ||
            this.categoryFilterResponseType == null
        )
            return true;
        else return false;
    }

    get isScoreOneToFive(): boolean {
        let result = this.categories.filter((x: CategoryModel) => x.responseType == ResponseTypeEnum.ScoreOneToFive);
        if (
            (result.length > 0 &&
                this.categoryFilterResponseType &&
                this.categoryFilterResponseType.length > 0 &&
                this.categoryFilterResponseType!.includes(ResponseTypeEnum.ScoreOneToFive)) ||
            this.categoryFilterResponseType == null
        )
            return true;
        else return false;
    }

    get isScoreZeroToThree(): boolean {
        let result = this.categories.filter((x: CategoryModel) => x.responseType == ResponseTypeEnum.ScoreZeroToThree);
        if (
            (result.length > 0 &&
                this.categoryFilterResponseType &&
                this.categoryFilterResponseType.length > 0 &&
                this.categoryFilterResponseType!.includes(ResponseTypeEnum.ScoreZeroToThree)) ||
            this.categoryFilterResponseType == null
        )
            return true;
        else return false;
    }

    get isScoreOneToFour(): boolean {
        let result = this.categories.filter((x: CategoryModel) => x.responseType == ResponseTypeEnum.ScoreOneToFour);
        if (
            (result.length > 0 &&
                this.categoryFilterResponseType &&
                this.categoryFilterResponseType.length > 0 &&
                this.categoryFilterResponseType!.includes(ResponseTypeEnum.ScoreOneToFour)) ||
            this.categoryFilterResponseType == null
        )
            return true;
        else return false;
    }

    get isVDA63(): boolean {
        let result = this.categories.filter((x: CategoryModel) => x.responseType == ResponseTypeEnum.VDA63);
        if (
            (result.length > 0 &&
                this.categoryFilterResponseType &&
                this.categoryFilterResponseType.length > 0 &&
                this.categoryFilterResponseType!.includes(ResponseTypeEnum.VDA63)) ||
            this.categoryFilterResponseType == null
        )
            return true;
        else return false;
    }

    get categoryFilterResponseType(): Array<string> | null | undefined {
        if (this.filterCategory) {
            let result = this.categories.filter((x: CategoryModel) => this.filterCategory!.includes(x.id!));
            if (result.length > 0) {
                let array: Array<string> = [];
                result.forEach((x: CategoryModel) => {
                    array.push(x.responseType!);
                });
                return array;
            } else return null;
        } else {
            return null;
        }
    }

    saveQuery() {
        this.setQuery('pageSize', this.pageSize?.toString());
        window.localStorage.ptPageSize = this.pageSize?.toString();
    }

    computeSuccessRate(responseCount: number, okCount: number): string {
        if (responseCount == 0) return '100';
        return ((okCount / responseCount) * 100).toFixed(2);
    }

    computeScoreSuccessRate(scoreValue: number, scoreMaxValue: number): string {
        if (scoreValue == null || scoreMaxValue == null) return '--';
        if (scoreMaxValue == 0) return '100 %';
        return ((scoreValue / scoreMaxValue) * 100).toFixed(2) + ' %';
    }

    mounted() {
        if (this.getQueryInt('pageSize') != null) {
            this.pageSize = this.getQueryInt('pageSize') ?? 25;
        } else {
            if (window.localStorage.ptPageSize && Number(window.localStorage.ptPageSize)) {
                this.pageSize = Number(window.localStorage.ptPageSize);
            }
            this.saveQuery();
        }
        if (this.isExternal) {
            this.filterOnlyMine = false;
        }
        this.load();
        this.loadCategories();
        if (this.isExternal) {
            this.showSecondLine = false;
            this.filterCategory = null;
            this.filterUser = null;
            this.filterDepartment = null;
            this.filterPosition = null;
        }
    }

    load() {
        planItemsStore.dispatch(CrudAction.GetAllBasic, this.getPayload());
    }

    getFilters(): CrudQueryPredicate[] {
        let filters: CrudQueryPredicate[] = [];
        let categoryFilters: CrudQueryPredicate[] = [];
        let auditFilters: CrudQueryPredicate[] = [];
        let userFilters: CrudQueryPredicate[] = [];
        let departmentFilters: CrudQueryPredicate[] = [];
        let positionFilters: CrudQueryPredicate[] = [];
        let workplaceFilters: CrudQueryPredicate[] = [];
        let groupFilters: CrudQueryPredicate[] = [];
        let stationFilters: CrudQueryPredicate[] = [];
        let planItemStateFilters: CrudQueryPredicate[] = [];
        if (!this.tenantUser.accessAllCategories) {
            filters.push({ field: 'audit.category.categoryUsers.tenantUserId', op: 'eq', comparand: this.tenantUser.id! });
        }
        if (this.filterOnlyMine && !this.isExternal) {
            filters.push({ field: 'planItemAuditTenantUsers.tenantUserId', op: 'eq', comparand: this.tenantUser.id! });
        }
        filters.push({ field: 'isAnswered', op: 'eq', comparand: true });
        filters.push({ field: 'processType', op: 'eq', comparand: ProcessTypeEnum.Audit });
        if (this.filterCategory && this.filterCategory.length > 0) {
            this.filterCategory.forEach((e: string) => {
                if (e) {
                    categoryFilters.push({
                        field: 'audit.categoryId',
                        op: 'eq',
                        comparand: e,
                        junction: 'or',
                    });
                }
            });
        }
        if (this.filterAudit && this.filterAudit.length > 0) {
            this.filterAudit.forEach((e: string) => {
                if (e) {
                    auditFilters.push({
                        field: 'auditId',
                        op: 'eq',
                        comparand: e,
                        junction: 'or',
                    });
                }
            });
        }
        if (this.filterUser && this.filterUser.length > 0) {
            this.filterUser.forEach((e: string) => {
                if (e) {
                    userFilters.push({
                        field: 'planItemAuditTenantUsers.tenantUserId',
                        op: 'eq',
                        comparand: e,
                        junction: 'or',
                    });
                }
            });
        }
        if (this.filterDepartment && this.filterDepartment.length > 0) {
            this.filterDepartment.forEach((e: string) => {
                if (e) {
                    departmentFilters.push({
                        field: 'tenantUser.departmentId',
                        op: 'eq',
                        comparand: e,
                        junction: 'or',
                    });
                }
            });
        }
        if (this.filterPosition && this.filterPosition.length > 0) {
            this.filterPosition.forEach((e: string) => {
                if (e) {
                    positionFilters.push({
                        field: 'tenantUser.positionId',
                        op: 'eq',
                        comparand: e,
                        junction: 'or',
                    });
                }
            });
        }
        const expandedLocationIds = this.computeExpandedLocationIds();
        if (expandedLocationIds.length > 0) {
            filters.push({ field: 'workplace.locationId', op: 'in', comparand: expandedLocationIds });
        }
        if (this.filterWorkplace && this.filterWorkplace.length > 0) {
            this.filterWorkplace.forEach((e: string) => {
                if (e) {
                    workplaceFilters.push({
                        field: 'workplaceId',
                        op: 'eq',
                        comparand: e,
                        junction: 'or',
                    });
                }
            });
        }
        if (this.filterGroup && this.filterGroup.length > 0) {
            this.filterGroup.forEach((e: string) => {
                if (e) {
                    groupFilters.push({
                        field: 'groupId',
                        op: 'eq',
                        comparand: e,
                        junction: 'or',
                    });
                    groupFilters.push({
                        field: 'auditTiming.groupId',
                        op: 'eq',
                        comparand: e,
                        junction: 'or',
                    });
                }
            });
        }
        if (this.filterStation && this.filterStation.length > 0) {
            this.filterStation.forEach((e: string) => {
                if (e) {
                    stationFilters.push({
                        field: 'stationId',
                        op: 'eq',
                        comparand: e,
                        junction: 'or',
                    });
                }
            });
        }
        if (this.filterPlanItemState && this.filterPlanItemState.length > 0) {
            this.filterPlanItemState.forEach((e: string) => {
                if (e) {
                    planItemStateFilters.push({
                        field: 'planItemState',
                        op: 'eq',
                        comparand: e,
                        junction: 'or',
                    });
                }
            });
        }
        if (this.filterResponseValues.length > 0) {
            const filterResponseValues = {
                field: 'id',
                op: 'wrap',
                comparand: [] as any[],
            };
            for (const filterResponseValue of this.filterResponseValues) {
                let column = '';
                switch (filterResponseValue) {
                    case 'ok':
                        column = 'OkCount';
                        break;
                    case 'okWithWarning':
                        column = 'OkWarningCount';
                        break;
                    case 'notOk':
                        column = 'NokCount';
                        break;
                    case 'scoreZero':
                        column = 'ScoreZeroCount';
                        break;
                    case 'scoreOne':
                        column = 'ScoreOneCount';
                        break;
                    case 'scoreTwo':
                        column = 'ScoreTwoCount';
                        break;
                    case 'scoreThree':
                        column = 'ScoreThreeCount';
                        break;
                    case 'scoreFour':
                        column = 'ScoreFourCount';
                        break;
                    case 'scoreFive':
                        column = 'ScoreFiveCount';
                        break;
                }
                filterResponseValues.comparand.push({ field: column, op: 'gt', comparand: 0, junction: 'or' });
            }
            filters.push(filterResponseValues);
        }
        if (this.filterDelay && !isNaN(this.filterDelay)) {
            filters.push({
                field: 'delayDays',
                op: 'gte',
                comparand: this.filterDelay,
            });
        }
        if (categoryFilters.length > 0) {
            filters.push({
                field: 'audit.categoryId',
                op: 'wrap',
                comparand: categoryFilters,
            });
        }
        if (auditFilters.length > 0) {
            filters.push({
                field: 'auditId',
                op: 'wrap',
                comparand: auditFilters,
            });
        }
        if (userFilters.length > 0) {
            filters.push({
                field: 'planItemAuditTenantUsers.tenantUserId',
                op: 'wrap',
                comparand: userFilters,
            });
        }
        if (departmentFilters.length > 0) {
            filters.push({
                field: 'tenantUser.departmentId',
                op: 'wrap',
                comparand: departmentFilters,
            });
        }
        if (positionFilters.length > 0) {
            filters.push({
                field: 'tenantUser.positionId',
                op: 'wrap',
                comparand: positionFilters,
            });
        }
        if (workplaceFilters.length > 0) {
            filters.push({
                field: 'workplaceId',
                op: 'wrap',
                comparand: workplaceFilters,
            });
        }
        if (groupFilters.length > 0) {
            filters.push({
                field: 'groupId',
                op: 'wrap',
                comparand: groupFilters,
            });
        }
        if (stationFilters.length > 0) {
            filters.push({
                field: 'stationId',
                op: 'wrap',
                comparand: stationFilters,
            });
        }
        if (planItemStateFilters.length > 0) {
            filters.push({
                field: 'planItemState',
                op: 'wrap',
                comparand: planItemStateFilters,
            });
        }
        if (this.filterEvidenceNumber && this.filterEvidenceNumber.length > 0) {
            filters.push({
                field: 'evidenceNumber',
                op: 'c',
                comparand: this.filterEvidenceNumber || '',
            });
        }
        return filters;
    }

    getPayload() {
        return createCrudQueryPayload<PlanItemModel>({ field: 'time', index: 1, order: 'desc' }, this.getFilters(), {
            pageIndex: this.pageIndex,
            pageSize: this.pageSize,
        });
    }

    exportClick() {
        const filters = this.getFilters();
        responsesStore.dispatch(
            CrudAction.Download,
            createCrudQueryPayload<ResponseModel>({ field: 'time', index: 1, order: 'desc' }, filters)
        );
    }

    loadCategories() {
        const filters: CrudQueryPredicate[] = [];
        if (!this.tenantUser.accessAllCategories) {
            filters.push({ field: 'categoryUsers.tenantUserId', op: 'eq', comparand: this.tenantUser.id! });
        }
        categoriesStore.dispatch(
            CrudAction.GetAll,
            createCrudQueryPayload<CategoryModel>({ field: 'name', index: 1, order: 'asc' }, filters)
        );
    }

    setShowInfo(code: ResponsesShowDetailTypeEnum): void {
        const show = !this.getShowInfo(code);
        responsesShowDetailsStore.dispatch(code, show);
    }

    getShowInfo(code: ResponsesShowDetailTypeEnum): boolean {
        return responsesShowDetailsStore.useGetter(code);
    }

    canShowPlanItemInfo(item: PlanItemModel): boolean {
        return (
            ((this.getShowInfo(ResponsesShowDetailTypeEnum.ShowDisagreementSummary) &&
                item.aiDisagreementSummary != null &&
                item.aiDisagreementSummary.length > 0) ||
                (this.getShowInfo(ResponsesShowDetailTypeEnum.ShowIdentifiedCauses) &&
                    item.aiIdentifiedCauses != null &&
                    item.aiIdentifiedCauses.length > 0) ||
                (this.getShowInfo(ResponsesShowDetailTypeEnum.ShowIdentifiedRisks) &&
                    item.aiIdentifiedRisks != null &&
                    item.aiIdentifiedRisks.length > 0) ||
                (this.getShowInfo(ResponsesShowDetailTypeEnum.ShowRecommendations) &&
                    item.aiRecommendations != null &&
                    item.aiRecommendations.length > 0) ||
                (this.getShowInfo(ResponsesShowDetailTypeEnum.ShowConclusion) &&
                    item.aiConclusion != null &&
                    item.aiConclusion.length > 0)) &&
            item.audit?.category?.aiUseReportGeneration == true
        );
    }

    get isExternal(): boolean {
        return this.tenantUser?.isExternal === true;
    }
}
